import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import type { ChatCostsResponse } from '../../types/admin';

interface ChatCostsTabProps {
  data: ChatCostsResponse;
}

const formatCost = (cost: number | null): string => {
  if (cost === null || cost === undefined) return 'N/A';
  return `$${cost.toFixed(4)}`;
};

const formatTokens = (tokens: number): string => {
  return tokens.toLocaleString();
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ChatCostsTab: React.FC<ChatCostsTabProps> = ({ data }) => {
  return (
    <Box>
      {/* Totals summary */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Totals: {data.totals.count} conversations |{' '}
          {formatTokens(data.totals.totalTokens)} tokens |{' '}
          Cost: {formatCost(data.totals.totalCost)}
        </Typography>
      </Paper>

      {/* Conversations table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Model</TableCell>
              <TableCell align="right">LLM Calls</TableCell>
              <TableCell align="right">Prompt Tokens</TableCell>
              <TableCell align="right">Completion Tokens</TableCell>
              <TableCell align="right">Total Tokens</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.conversations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No chat cost data available yet. Token tracking starts after deployment.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.conversations.map((conv) => (
                <TableRow key={conv.conversationId} hover>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                      {conv.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {conv.model}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{conv.tokenUsage.callCount || '—'}</TableCell>
                  <TableCell align="right">{formatTokens(conv.tokenUsage.promptTokens)}</TableCell>
                  <TableCell align="right">{formatTokens(conv.tokenUsage.completionTokens)}</TableCell>
                  <TableCell align="right">{formatTokens(conv.tokenUsage.totalTokens)}</TableCell>
                  <TableCell align="right">{formatCost(conv.cost)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {formatDate(conv.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ChatCostsTab;
